import { paths } from "./types";

/**
 * Type from request format
 * Note: Does not always have a body
 */
type Req<P extends keyof paths, M extends keyof paths[P]> =
  paths[P][M] extends {
    requestBody: {
      content: {
        "application/json": infer R;
      };
    };
  }
    ? R
    : undefined;

/**
 * Type from response format
 */
type Res<
  P extends keyof paths,
  M extends keyof paths[P]
> = paths[P][M] extends { responses: { 200: { content: { "application/json": infer R } } } }
  ? R
  : never;

type PostablePaths = {
  [P in keyof paths]: "post" extends keyof paths[P] ? P : never
}[keyof paths];

type GetablePaths = {
  [P in keyof paths]: "get" extends keyof paths[P] ? P : never
}[keyof paths];

type LoadVizPaths = {
  [P in keyof paths]: "get" extends keyof paths[P] ?  P : never
}[keyof paths];

/**
 * Generic load viz function
 */
const loadViz = async function <P extends LoadVizPaths>(
  path: P,
  signal?: AbortSignal,
// ): Promise<VizRes<P, "get">> {
): Promise<string> {
  const res = await fetch(`${path}`, {
    method: "GET",
    headers: { "Accept": "text/plain" },
    signal,
  });

  if (!res.ok) {
    const errText = await res.text();
    throw new Error(JSON.stringify({ status: res.status, message: errText }));
  }

  const html = await res.text();
  return html;
}

/**
 * Viz GET call function factory
 * @param path API path
 * @returns GET call function
 */
export const loadVizFactory = <P extends LoadVizPaths>(path: P) => {
  return async (signal?: AbortSignal) => {
    return loadViz(path, signal);
  };
};

// TODO (SID): end of example section

/**
 * Generic POST call function
 * @param path API path
 * @param body Request body
 * @param signal Abort signal
 * @returns Response body
 */
const post = async function <P extends PostablePaths>(
  path: P,
  body?: Req<P, "post">,
  signal?: AbortSignal,
): Promise<Res<P, "post">> {
  const res = await fetch(`${path}`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: body !== undefined ? JSON.stringify(body) : undefined,
    signal,
  });

  if (!res.ok) {
    const errText = await res.text();
    throw new Error(JSON.stringify({ status: res.status, message: errText }));
  }

  return res.json();
};

/**
 * Generic GET call function
 * @param path API path
 * @param signal Abort signal
 * @returns Response body
 */
const get = async function <P extends GetablePaths>(
  path: P,
  signal?: AbortSignal,
): Promise<Res<P, "get">> {
  const res = await fetch(`${path}`, {
    method: "GET",
    headers: { "Accept": "application/json" },
    signal,
  });

  if (!res.ok) {
    const errText = await res.text();
    throw new Error(JSON.stringify({ status: res.status, message: errText }));
  }

  return res.json();
};

/**
 * Generic POST call function factory
 * @param path API path
 * @returns POST call function
 */
export const postFactory = <P extends PostablePaths>(path: P) => {
  return async (body?: Req<P, "post">, signal?: AbortSignal) => {
    return post(path, body, signal);
  };
};

/**
 * Generic GET call function factory
 * @param path API path
 * @returns GET call function
 */
export const getFactory = <P extends GetablePaths>(path: P) => {
  return async (signal?: AbortSignal) => {
    return get(path, signal);
  };
};
